<!-- BarChart.vue -->
 <!-- 柱状图 -->
<template>
  <div class="bar-chart-container">
    <div ref="chart" :style="{ width: width, height: height }"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'EchartsBarChart',
  props: {
    // 尺寸配置
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '200px'
    },
    // 数据配置
    xAxisData: {
      type: Array,
      default: () => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    },
    seriesData: {
      type: [Array, Object],
      default: () => [120, 200, 150, 80, 70, 110, 130]
    },
    // 样式配置
    showBackground: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: 'rgba(180, 180, 180, 0.2)'
    },
    barColor: {
      type: String,
      default: '#5470c6'
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    // 图例位置配置
    legendPosition: {
      type: String,
      default: 'top-right',
      validator: (value) => {
        return ['top-right', 'top-left', 'bottom', 'top', 'right', 'left'].includes(value);
      }
    },
    // 标题配置
    title: {
      type: String,
      default: ''
    },
    // 新增：标题位置配置
    titlePosition: {
      type: String,
      default: 'center',
      validator: (value) => {
        return ['left', 'center', 'right'].includes(value);
      }
    },
    // 新增：标题对齐方式
    titleAlign: {
      type: String,
      default: 'center',
      validator: (value) => {
        return ['left', 'center', 'right'].includes(value);
      }
    },
    // 新增：标题距离顶部距离
    titleTop: {
      type: [String, Number],
      default: '10px'
    },
    // 纵坐标配置
    yAxisName: {
      type: String,
      default: ''
    },
    yAxisUnit: {
      type: String,
      default: ''
    },
    showYAxisSplitNumber: {
      type: Boolean,
      default: false
    },
    // 下载相关属性
    downloadFileName: {
      type: String,
      default: 'bar_chart'
    },
    downloadType: {
      type: String,
      default: 'png',
      validator: (value) => ['png', 'jpeg'].includes(value)
    },
    // 是否显示工具箱（下载按钮）
    showToolbox: {
      type: Boolean,
      default: true
    },
    // 新增：单系列图例名称
    legendName: {
      type: String,
      default: '数据'
    },
    // 新增：自定义图例数据
    customLegendData: {
      type: Array,
      default: () => []
    },
    // 新增：颜色序列配置
    colors: {
      type: Array,
      default: () => [
        '#5470c6', '#91cc75', '#fac858', '#ee6666', 
        '#73c0de', '#3ba272', '#fc8452', '#9a60b4',
        '#ea7ccc', '#7ebf42', '#e6d450', '#f2a7c3',
        '#b6a2de', '#5ab1ef', '#ffb980', '#d87a80'
      ]
    },
    // 新增：图表边距配置
    chartMargin: {
      type: [String, Number],
      default: '5%'
    },
    // 新增：图表顶部边距（考虑标题和图例）
    chartTopMargin: {
      type: [String, Number],
      default: 'auto'
    },
    // 新增：图表底部边距
    chartBottomMargin: {
      type: [String, Number],
      default: 'auto'
    }
  },
  data() {
    return {
      chart: null
    };
  },
  mounted() {
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    // 下载图片功能
    downloadImage() {
      if (!this.chart) {
        console.warn('图表实例未初始化');
        return;
      }

      try {
        const dataURL = this.chart.getDataURL({
          type: this.downloadType,
          pixelRatio: 2,
          backgroundColor: '#ffffff',
          excludeComponents: ['toolbox']
        });

        const link = document.createElement('a');
        link.href = dataURL;
        link.download = `${this.downloadFileName}_${new Date().getTime()}.${this.downloadType}`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('图表下载成功');
      } catch (error) {
        console.error('图表下载失败:', error);
        alert('下载失败，请重试');
      }
    },

    // 获取工具箱配置
    getToolboxConfig() {
      if (!this.showToolbox) {
        return undefined;
      }
      
      return {
        feature: {
          saveAsImage: {
            title: '保存为图片',
            type: this.downloadType,
            name: this.downloadFileName,
            pixelRatio: 2,
            backgroundColor: '#ffffff',
            excludeComponents: ['toolbox'],
          }
        },
        right: 10,
        top: 10,
        iconStyle: {
          borderColor: '#666'
        }
      };
    },

    initChart() {
      this.chart = echarts.init(this.$refs.chart);
      this.updateChart();
    },
    
    // 判断数据格式
    isMultiSeries() {
      return Array.isArray(this.seriesData) && 
             this.seriesData.length > 0 && 
             typeof this.seriesData[0] === 'object' && 
             'data' in this.seriesData[0];
    },
    
    // 获取默认颜色
    getDefaultColor(index) {
      const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4'];
      return colors[index % colors.length];
    },
    
    // 获取图例数据
    getLegendData() {
      // 如果有自定义图例数据，直接使用
      if (this.customLegendData && this.customLegendData.length > 0) {
        return this.customLegendData;
      }
      
      // 如果是多系列数据，从seriesData中提取
      if (this.isMultiSeries()) {
        return this.seriesData.map(item => item.name || '');
      }
      
      // 单系列数据，使用legendName
      return [this.legendName];
    },
    
    // 获取图例配置
    getLegendConfig() {
      // 如果不显示图例，返回undefined
      if (!this.showLegend) {
        return undefined;
      }
      
      const legendData = this.getLegendData();
      
      // 如果没有图例数据，返回undefined
      if (!legendData || legendData.length === 0) {
        return undefined;
      }
      
      // 根据位置配置返回不同的图例设置
      switch (this.legendPosition) {
        case 'top-right':
          return {
            data: legendData,
            right: '3%',
            top: '8%',  // 修改：增加一点顶部间距
            orient: 'horizontal',
            padding: [5, 5, 5, 5],  // 添加内边距
            itemGap: 15  // 图例项间距
          };
        case 'top-left':
          return {
            data: legendData,
            left: '3%',
            top: '3%',  // 修改：增加一点顶部间距
            orient: 'horizontal',
            padding: [5, 5, 5, 5],
            itemGap: 15
          };
        case 'bottom':
          return {
            data: legendData,
            bottom: '3%',  // 修改：增加一点底部间距
            left: 'center',
            orient: 'horizontal',
            padding: [5, 5, 5, 5],
            itemGap: 15
          };
        case 'top':
          return {
            data: legendData,
            top: '3%',  // 修改：增加一点顶部间距
            left: 'center',
            orient: 'horizontal',
            padding: [5, 5, 5, 5],
            itemGap: 15
          };
        case 'right':
          return {
            data: legendData,
            right: '3%',
            top: 'center',
            orient: 'vertical',
            padding: [5, 5, 5, 5],
            itemGap: 15
          };
        case 'left':
          return {
            data: legendData,
            left: '3%',
            top: 'center',
            orient: 'vertical',
            padding: [5, 5, 5, 5],
            itemGap: 15
          };
        default:
          return {
            data: legendData,
            right: '3%',
            top: '3%',  // 修改：增加一点顶部间距
            orient: 'horizontal',
            padding: [5, 5, 5, 5],
            itemGap: 15
          };
      }
    },

    // 获取网格配置（居中显示）
    getGridConfig() {
      // 基础网格配置 - 默认居中显示
      const baseGrid = {
        left: this.chartMargin,
        right: this.chartMargin,
        containLabel: true
      };

      // 获取图例数据
      const legendData = this.getLegendData();
      const hasLegend = this.showLegend && legendData && legendData.length > 0;
      const hasTitle = !!this.title;

      // 如果指定了顶部和底部边距，直接使用
      if (this.chartTopMargin !== 'auto' || this.chartBottomMargin !== 'auto') {
        return {
          ...baseGrid,
          top: this.chartTopMargin !== 'auto' ? this.chartTopMargin : '10%',
          bottom: this.chartBottomMargin !== 'auto' ? this.chartBottomMargin : '10%'
        };
      }

      // 自动计算边距以实现居中效果
      let top = '10%';
      let bottom = '10%';
      
      if (hasLegend) {
        // 根据图例位置调整
        switch (this.legendPosition) {
          case 'top-right':
          case 'top-left':
          case 'top':
            // 顶部有图例，增加顶部边距
            top = hasTitle ? '25%' : '20%';  // 有标题时再多留一些空间
            bottom = '8%';
            break;
          case 'bottom':
            // 底部有图例，增加底部边距
            top = hasTitle ? '15%' : '8%';
            bottom = '20%';
            break;
          case 'right':
            // 右侧有图例，调整左右边距
            baseGrid.right = '15%';  // 右侧多留空间给图例
            baseGrid.left = '8%';
            top = hasTitle ? '15%' : '10%';
            bottom = '10%';
            break;
          case 'left':
            // 左侧有图例，调整左右边距
            baseGrid.left = '15%';  // 左侧多留空间给图例
            baseGrid.right = '8%';
            top = hasTitle ? '15%' : '10%';
            bottom = '10%';
            break;
          default:
            top = hasTitle ? '20%' : '15%';
            bottom = '8%';
        }
      } else {
        // 没有图例的情况
        if (hasTitle) {
          top = '15%';
          bottom = '8%';
        } else {
          // 既没有图例也没有标题，完全居中
          top = '10%';
          bottom = '10%';
        }
      }

      return {
        ...baseGrid,
        top: top,
        bottom: bottom
      };
    },

    // 获取网格顶部距离（考虑标题位置）
    getGridTop() {
      if (!this.title) return '10%';  // 修改：默认10%居中
      
      // 如果标题在顶部，需要为标题留出空间
      if (this.titleTop === 'auto' || typeof this.titleTop === 'string') {
        return '60px'; // 默认标题高度
      } else if (typeof this.titleTop === 'number') {
        // 数值类型的 top，加上一些额外空间
        return this.titleTop + 40 + 'px';
      } else {
        // 字符串类型的 top
        if (this.titleTop.includes('px')) {
          const pxValue = parseInt(this.titleTop);
          return (pxValue + 40) + 'px';
        } else if (this.titleTop.includes('%')) {
          // 百分比类型
          const percentValue = parseInt(this.titleTop);
          return (percentValue + 15) + '%';
        } else {
          return '60px'; // 默认
        }
      }
    },

    // 获取标题配置
    getTitleConfig() {
      if (!this.title) return undefined;
      
      const titleConfig = {
        text: this.title,
        textStyle: {
          fontSize: '18px',
          color: '#666'
        },
        top: this.titleTop,
        textAlign: this.titleAlign
      };
      
      // 设置水平位置
      switch (this.titlePosition) {
        case 'left':
          titleConfig.left = 'left';
          break;
        case 'center':
          titleConfig.left = 'center';
          break;
        case 'right':
          titleConfig.left = 'right';
          break;
      }
      
      return titleConfig;
    },
    
    updateChart() {
      let series = [];
      
      if (this.isMultiSeries()) {
        // 多系列数据格式
        series = this.seriesData.map((item, index) => ({
          name: item.name || `系列${index + 1}`,
          data: item.data,
          type: 'bar',
          showBackground: this.showBackground,
          backgroundStyle: {
            color: this.backgroundColor
          },
          itemStyle: {
            color: item.color || this.getDefaultColor(index)
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }));
      } else {
        // 单系列数据格式
        const legendData = this.getLegendData();
        series = [
          {
            name: legendData[0] || this.legendName, // 设置系列名称，用于图例显示
            data: this.seriesData,
            type: 'bar',
            showBackground: this.showBackground,
            backgroundStyle: {
              color: this.backgroundColor
            },
            itemStyle: {
              color: this.barColor
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ];
      }

      // 配置纵坐标
      const yAxisConfig = {
        type: 'value',
        name: this.yAxisName,
        nameLocation: 'end',
        nameTextStyle: {
          color: '#666',
          fontSize: 12,
          padding: [0, 0, 0, 0]
        }
      };

      // 如果设置了单位，在坐标轴标签上显示单位
      if (this.yAxisUnit) {
        yAxisConfig.axisLabel = {
          formatter: (value) => {
            let formattedValue = value;
            if (this.showYAxisSplitNumber) {
              formattedValue = value.toLocaleString();
            }
            return `${formattedValue}${this.yAxisUnit}`;
          }
        };
      } else if (this.showYAxisSplitNumber) {
        yAxisConfig.axisLabel = {
          formatter: (value) => {
            return value.toLocaleString();
          }
        };
      }

      const option = {
        title: this.getTitleConfig(),
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            if (this.isMultiSeries()) {
              let result = `${params[0].name}<br/>`;
              params.forEach(item => {
                const value = this.showYAxisSplitNumber ? item.value.toLocaleString() : item.value;
                result += `${item.marker} ${item.seriesName}: ${value}${this.yAxisUnit}<br/>`;
              });
              return result;
            } else {
              const value = this.showYAxisSplitNumber ? params[0].value.toLocaleString() : params[0].value;
              const seriesName = params[0].seriesName || this.legendName;
              return `${params[0].name}<br/>${params[0].marker} ${seriesName}: ${value}${this.yAxisUnit}`;
            }
          }
        },
        xAxis: {
          type: 'category',
          data: this.xAxisData,
          axisLabel: {
            interval: 0 // 强制显示所有标签
          }
        },
        yAxis: yAxisConfig,
        series: series,
        legend: this.getLegendConfig(),
        grid: this.getGridConfig(),
        toolbox: this.getToolboxConfig()
      };
      
      this.chart.setOption(option);
    },
    
    handleResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    
    // 提供给父组件调用的方法
    setOption(option) {
      if (this.chart) {
        this.chart.setOption(option);
      }
    },
    
    getInstance() {
      return this.chart;
    },
    
    resize() {
      if (this.chart) {
        this.chart.resize();
      }
    }
  },
  watch: {
    xAxisData: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    seriesData: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    yAxisName: {
      handler() {
        this.updateChart();
      }
    },
    yAxisUnit: {
      handler() {
        this.updateChart();
      }
    },
    showYAxisSplitNumber: {
      handler() {
        this.updateChart();
      }
    },
    legendPosition: {
      handler() {
        this.updateChart();
      }
    },
    showLegend: {
      handler() {
        this.updateChart();
      }
    },
    showToolbox: {
      handler() {
        this.updateChart();
      }
    },
    // 新增标题相关属性的监听
    title: {
      handler() {
        this.updateChart();
      }
    },
    titlePosition: {
      handler() {
        this.updateChart();
      }
    },
    titleAlign: {
      handler() {
        this.updateChart();
      }
    },
    titleTop: {
      handler() {
        this.updateChart();
      }
    },
    // 新增图例相关属性的监听
    legendName: {
      handler() {
        this.updateChart();
      }
    },
    customLegendData: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    // 新增边距相关属性的监听
    chartMargin: {
      handler() {
        this.updateChart();
      }
    },
    chartTopMargin: {
      handler() {
        this.updateChart();
      }
    },
    chartBottomMargin: {
      handler() {
        this.updateChart();
      }
    }
  },
  expose: ['downloadImage', 'resize', 'getInstance', 'setOption']
}
</script>

<style scoped>
.bar-chart-container {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>